import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  CheckCircle2, UserCheck, FileSignature, Upload, GraduationCap,
  ArrowRight, Download, Loader2, AlertCircle, Lock, ChevronRight, Edit2, Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: 'profile_complete', label: 'Complete Profile', icon: UserCheck, desc: 'Fill in your license and contact details', time: '~2 min' },
  { key: 'ica_signed', label: 'Sign ICA', icon: FileSignature, desc: 'Review & sign the Independent Contractor Agreement', time: '~5 min' },
  { key: 'documents_uploaded', label: 'Upload Documents', icon: Upload, desc: 'Upload your license and required documents', time: '~5 min' },
  { key: 'orientation_complete', label: 'Orientation', icon: GraduationCap, desc: 'Complete your agent orientation', time: '~3 min' },
];

export default function OnboardingFlow({ onComplete }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [icaStatus, setIcaStatus] = useState(null);
  const [agreementState, setAgreementState] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [sigName, setSigName] = useState('');
  const [signing, setSigning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [editingApp, setEditingApp] = useState(false);
  const [appForm, setAppForm] = useState({});
  const [savingApp, setSavingApp] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const [obRes, icaRes, agreeRes, appRes] = await Promise.all([
        axios.get(`${API}/onboarding/status`, { withCredentials: true }),
        axios.get(`${API}/ica/status`, { withCredentials: true }),
        axios.get(`${API}/ica/agreement-state`, { withCredentials: true }),
        axios.get(`${API}/applications`, { withCredentials: true }),
      ]);
      setStatus(obRes.data);
      setIcaStatus(icaRes.data);
      setAgreementState(agreeRes.data);
      if (appRes.data.length > 0) {
        const app = appRes.data[0];
        setApplication(app);
        setAppForm({ first_name: app.first_name || '', last_name: app.last_name || '', phone: app.phone || '', license_number: app.license_number || '', license_state: app.license_state || '', experience_years: app.experience_years || 0, specialization: app.specialization || '', preferred_location: app.preferred_location || '', about: app.about || '' });
      }
      const s = obRes.data;
      if (!s.profile_complete) setActiveStep(0);
      else if (!s.ica_signed) setActiveStep(1);
      else if (!s.documents_uploaded) setActiveStep(2);
      else if (!s.orientation_complete) setActiveStep(3);
      else setActiveStep(4);
    } catch { /* fetch error */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const completeStep = async (stepKey) => {
    try {
      await axios.post(`${API}/onboarding/complete-step`, { step: stepKey }, { withCredentials: true });
      toast.success('Step completed!');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete step');
    }
  };

  const handleSignICA = async () => {
    if (!sigName.trim()) { toast.error('Please type your full legal name'); return; }
    setSigning(true);
    try {
      await axios.post(`${API}/ica/sign`, { signature_name: sigName.trim() }, { withCredentials: true });
      toast.success('ICA signed successfully!');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign ICA');
    } finally {
      setSigning(false);
    }
  };

  const previewICA = () => { window.open(`${API}/ica/preview`, '_blank'); };
  const downloadSignedICA = () => { window.open(`${API}/ica/download`, '_blank'); };

  const handleSaveApplication = async () => {
    setSavingApp(true);
    try {
      const res = await axios.put(`${API}/applications/${application.id}`, appForm, { withCredentials: true });
      setApplication(res.data);
      setEditingApp(false);
      toast.success('Application updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update application');
    } finally { setSavingApp(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-slate-400" /></div>;
  }

  const completedCount = STEPS.filter(s => status?.[s.key]).length;
  const allComplete = completedCount === 4;

  if (allComplete) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12" data-testid="onboarding-complete">
        <div className="w-20 h-20 bg-emerald-50 border-2 border-emerald-200 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 size={36} className="text-emerald-600" />
        </div>
        <h2 className="text-3xl font-['Cabinet_Grotesk'] font-bold text-slate-900 mb-2">You're All Set</h2>
        <p className="text-slate-500 mb-8 max-w-sm mx-auto">Your onboarding is complete. Welcome to Krieger Real Estate!</p>
        {onComplete && (
          <Button onClick={onComplete} className="bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg px-8 py-3" data-testid="onboarding-done-btn">
            Go to Dashboard <ArrowRight size={14} className="ml-2" />
          </Button>
        )}
      </motion.div>
    );
  }

  const getStepState = (i) => {
    const done = status?.[STEPS[i].key];
    if (done) return 'done';
    if (i === activeStep) return 'active';
    // Locked if any prior step is incomplete
    for (let j = 0; j < i; j++) {
      if (!status?.[STEPS[j].key]) return 'locked';
    }
    return 'upcoming';
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="onboarding-flow">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <h1 className="text-3xl font-['Cabinet_Grotesk'] font-bold text-slate-900">Agent Onboarding</h1>
          <span className="text-xs font-semibold bg-[#EFF3FF] text-[#002FA7] px-2.5 py-1 rounded-full">{completedCount}/4</span>
        </div>
        <p className="text-sm text-slate-500 mb-4">Complete each step below to activate your agent account.</p>
        {/* Overall progress */}
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden" data-testid="onboarding-progress">
          <motion.div
            className="h-full bg-gradient-to-r from-[#002FA7] to-[#38BDF8] rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${(completedCount / 4) * 100}%` }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Two-column layout: Timeline left, Content right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ── Left: Vertical Timeline ── */}
        <div className="lg:col-span-4 bg-[#F0F4FF] border border-[#D6E0FF] rounded-2xl p-5 space-y-0" data-testid="onboarding-timeline">
          {STEPS.map((step, i) => {
            const state = getStepState(i);
            const isLast = i === STEPS.length - 1;
            return (
              <div key={step.key} className="relative">
                {/* Connector line */}
                {!isLast && (
                  <div className={`absolute left-[19px] top-[48px] w-0.5 h-[calc(100%-16px)] transition-colors duration-300 ${state === 'done' ? 'bg-emerald-400' : 'bg-[#C7D4F0]'}`} />
                )}
                <button
                  onClick={() => state !== 'locked' && setActiveStep(i)}
                  disabled={state === 'locked'}
                  className={`w-full flex items-start gap-4 p-3 rounded-xl transition-all text-left group
                    ${state === 'active' ? 'bg-white border border-[#002FA7]/20 shadow-sm' : ''}
                    ${state === 'done' ? 'bg-white/60 border border-transparent hover:border-emerald-300' : ''}
                    ${state === 'locked' ? 'opacity-40 cursor-not-allowed border border-transparent' : ''}
                    ${state === 'upcoming' ? 'bg-transparent border border-transparent hover:bg-white/50' : ''}
                  `}
                  data-testid={`onboarding-step-${i}`}
                >
                  {/* Step circle */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all
                    ${state === 'done' ? 'bg-emerald-500 text-white' : ''}
                    ${state === 'active' ? 'bg-[#002FA7] text-white shadow-md shadow-[#002FA7]/25' : ''}
                    ${state === 'locked' ? 'bg-[#D6E0FF] text-[#8EA4D8]' : ''}
                    ${state === 'upcoming' ? 'bg-[#D6E0FF] text-[#7B93C9]' : ''}
                  `}>
                    {(() => {
                      if (state === 'done') return <CheckCircle2 size={18} />;
                      if (state === 'locked') return <Lock size={14} />;
                      return <step.icon size={18} />;
                    })()}
                  </div>
                  {/* Text */}
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold transition-colors
                        ${state === 'active' ? 'text-[#002FA7]' : ''}
                        ${state === 'done' ? 'text-emerald-700' : ''}
                        ${state !== 'active' && state !== 'done' ? 'text-slate-500' : ''}
                      `}>{step.label}</span>
                      {state === 'done' && <span className="text-[9px] bg-emerald-100 text-emerald-600 px-1.5 py-0.5 rounded-full font-semibold">Done</span>}
                    </div>
                    <p className={`text-xs mt-0.5 leading-relaxed ${state === 'active' ? 'text-slate-600' : 'text-slate-400'}`}>{step.desc}</p>
                    {state !== 'done' && state !== 'locked' && (
                      <span className="text-[9px] text-slate-300 mt-1 block">{step.time}</span>
                    )}
                  </div>
                  {/* Arrow indicator */}
                  {state === 'active' && (
                    <ChevronRight size={16} className="text-[#002FA7] mt-1 shrink-0 hidden lg:block" />
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* ── Right: Step Content Panel ── */}
        <div className="lg:col-span-8">
          <AnimatePresence mode="wait">
            {/* Step 1: Profile */}
            {activeStep === 0 && (
              <motion.div key="s0" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}
                className="bg-white border border-slate-200 rounded-xl p-6 md:p-8" data-testid="onboarding-step-profile">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#EFF3FF] flex items-center justify-center">
                    <UserCheck size={22} className="text-[#002FA7]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-['Cabinet_Grotesk'] font-bold text-slate-900">Complete Your Profile</h3>
                    <p className="text-sm text-slate-400">Step 1 of 4</p>
                  </div>
                </div>

                {/* Application Info Section */}
                {application && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-slate-800">Application Information</h4>
                      {!editingApp ? (
                        <Button variant="outline" size="sm" onClick={() => setEditingApp(true)} className="text-xs border-slate-200 text-slate-500 rounded-lg" data-testid="edit-application-btn">
                          <Edit2 size={12} className="mr-1.5" /> Edit
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setEditingApp(false); setAppForm({ first_name: application.first_name || '', last_name: application.last_name || '', phone: application.phone || '', license_number: application.license_number || '', license_state: application.license_state || '', experience_years: application.experience_years || 0, specialization: application.specialization || '', preferred_location: application.preferred_location || '', about: application.about || '' }); }} className="text-xs border-slate-200 text-slate-400 rounded-lg" data-testid="cancel-edit-btn">Cancel</Button>
                          <Button size="sm" onClick={handleSaveApplication} disabled={savingApp} className="text-xs bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg" data-testid="save-application-btn">
                            <Save size={12} className="mr-1.5" />{savingApp ? 'Saving...' : 'Save'}
                          </Button>
                        </div>
                      )}
                    </div>

                    {editingApp ? (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 space-y-4" data-testid="edit-application-form">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">First Name</Label>
                            <Input value={appForm.first_name} onChange={(e) => setAppForm(f => ({ ...f, first_name: e.target.value }))} className="bg-white border-slate-200 text-slate-800 rounded-lg" data-testid="app-first-name" />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Last Name</Label>
                            <Input value={appForm.last_name} onChange={(e) => setAppForm(f => ({ ...f, last_name: e.target.value }))} className="bg-white border-slate-200 text-slate-800 rounded-lg" data-testid="app-last-name" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Phone</Label>
                            <Input value={appForm.phone} onChange={(e) => setAppForm(f => ({ ...f, phone: e.target.value }))} className="bg-white border-slate-200 text-slate-800 rounded-lg" data-testid="app-phone" />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">License Number</Label>
                            <Input value={appForm.license_number} onChange={(e) => setAppForm(f => ({ ...f, license_number: e.target.value }))} className="bg-white border-slate-200 text-slate-800 rounded-lg" data-testid="app-license-number" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">License State</Label>
                            <Select value={appForm.license_state || ''} onValueChange={(v) => setAppForm(f => ({ ...f, license_state: v }))}>
                              <SelectTrigger className="bg-white border-slate-200 text-slate-800 rounded-lg" data-testid="app-license-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                              <SelectContent className="bg-white border-slate-200"><SelectItem value="NY">New York</SelectItem><SelectItem value="PA">Pennsylvania</SelectItem><SelectItem value="FL">Florida</SelectItem></SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Specialization</Label>
                            <Select value={appForm.specialization || ''} onValueChange={(v) => setAppForm(f => ({ ...f, specialization: v }))}>
                              <SelectTrigger className="bg-white border-slate-200 text-slate-800 rounded-lg" data-testid="app-specialization"><SelectValue placeholder="Select focus" /></SelectTrigger>
                              <SelectContent className="bg-white border-slate-200"><SelectItem value="multifamily">Multifamily</SelectItem><SelectItem value="office">Office Buildings</SelectItem><SelectItem value="retail_restaurant">Retail / Restaurant</SelectItem><SelectItem value="self_storage">Self Storage</SelectItem><SelectItem value="warehouse_industrial">Warehouse / Industrial</SelectItem><SelectItem value="mixed_commercial">Mixed Commercial</SelectItem></SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Preferred Location</Label>
                          <Select value={appForm.preferred_location || ''} onValueChange={(v) => setAppForm(f => ({ ...f, preferred_location: v }))}>
                            <SelectTrigger className="bg-white border-slate-200 text-slate-800 rounded-lg" data-testid="app-preferred-location"><SelectValue placeholder="Where do you operate?" /></SelectTrigger>
                            <SelectContent className="bg-white border-slate-200"><SelectItem value="New York">New York</SelectItem><SelectItem value="Pennsylvania">Pennsylvania</SelectItem><SelectItem value="Florida">Florida</SelectItem></SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">About</Label>
                          <Textarea value={appForm.about || ''} onChange={(e) => setAppForm(f => ({ ...f, about: e.target.value }))} className="bg-white border-slate-200 text-slate-800 rounded-lg min-h-[80px]" placeholder="Tell us about yourself..." data-testid="app-about" />
                        </div>
                      </div>
                    ) : (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-5" data-testid="application-info">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                          {[
                            { label: 'Name', value: `${application.first_name} ${application.last_name}` },
                            { label: 'Phone', value: application.phone },
                            { label: 'License #', value: application.license_number || '—' },
                            { label: 'License State', value: application.license_state || '—' },
                            { label: 'Specialization', value: application.specialization?.replace(/_/g, ' ') || '—' },
                            { label: 'Location', value: application.preferred_location || '—' },
                          ].map(f => (
                            <div key={f.label}>
                              <span className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{f.label}</span>
                              <div className="text-sm text-slate-800 capitalize">{f.value}</div>
                            </div>
                          ))}
                        </div>
                        {application.about && <div className="mt-3 pt-3 border-t border-slate-200"><span className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">About</span><p className="text-sm text-slate-600 mt-0.5">{application.about}</p></div>}
                      </div>
                    )}
                  </div>
                )}

                {status?.profile_complete ? (
                  <div className="flex items-center gap-3 text-emerald-600 bg-emerald-50 border border-emerald-200 p-4 rounded-lg"><CheckCircle2 size={20} /><span className="text-sm font-medium">Profile completed</span></div>
                ) : (
                  <div>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 mb-6">
                      <h4 className="text-sm font-semibold text-slate-800 mb-3">What you need to do:</h4>
                      <div className="space-y-2">
                        {['Go to the Profile tab in the left sidebar', 'Enter your phone number and license details', 'Select your license state and specialization', 'Return here and mark this step complete'].map((item, i) => (
                          <div key={`profile-step-${i}`} className="flex items-start gap-2.5">
                            <span className="w-5 h-5 rounded-full bg-[#002FA7] text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                            <span className="text-sm text-slate-600">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Button onClick={() => completeStep('profile_complete')} className="bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg px-6 py-2.5" data-testid="complete-profile-btn">
                      I've Completed My Profile <ArrowRight size={14} className="ml-2" />
                    </Button>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 2: ICA */}
            {activeStep === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}
                className="bg-white border border-slate-200 rounded-xl p-6 md:p-8" data-testid="onboarding-step-ica">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#EFF3FF] flex items-center justify-center">
                    <FileSignature size={22} className="text-[#002FA7]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-['Cabinet_Grotesk'] font-bold text-slate-900">Independent Contractor Agreement</h3>
                    <p className="text-sm text-slate-400">Step 2 of 4</p>
                  </div>
                </div>

                {icaStatus?.signed ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-emerald-600 bg-emerald-50 border border-emerald-200 p-4 rounded-lg">
                      <CheckCircle2 size={20} />
                      <div>
                        <span className="text-sm font-medium block">ICA Signed Successfully</span>
                        <span className="text-xs text-emerald-500">Signed on {new Date(icaStatus.signed_at).toLocaleDateString()} by {icaStatus.signature_name}</span>
                      </div>
                    </div>
                    <Button variant="outline" onClick={downloadSignedICA} className="border-slate-200 text-slate-600 rounded-lg" data-testid="download-ica-btn">
                      <Download size={14} className="mr-2" /> Download Signed Copy
                    </Button>
                  </div>
                ) : !agreementState?.has_agreement ? (
                  <div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle size={16} className="text-amber-500" />
                        <span className="text-sm font-semibold text-slate-800">License State Required</span>
                      </div>
                      <p className="text-sm text-slate-600">Please set your license state (New York, Florida, or Pennsylvania) in your Profile before reviewing and signing the agreement. Each state has a specific Independent Contractor Agreement.</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    {/* State-specific agreement badge */}
                    <div className="bg-[#EFF3FF] border border-[#002FA7]/10 rounded-lg px-4 py-2.5 mb-5 inline-flex items-center gap-2">
                      <span className="text-xs font-semibold text-[#002FA7]">{agreementState?.state_name} Agreement</span>
                    </div>

                    {/* Agreement highlights */}
                    <div className="grid grid-cols-2 gap-3 mb-6">
                      {[
                        { label: 'Commission', value: '90/10 Split', sub: 'On all transactions' },
                        { label: 'Fees', value: '$0/month', sub: 'No desk or monthly fees' },
                        { label: 'Status', value: 'Independent', sub: 'Contractor agreement' },
                        { label: 'State', value: agreementState?.state_name || '', sub: 'Governing law' },
                      ].map(item => (
                        <div key={item.label} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                          <span className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{item.label}</span>
                          <div className="text-base font-['Cabinet_Grotesk'] font-bold text-slate-900 mt-0.5">{item.value}</div>
                          <span className="text-[10px] text-slate-400">{item.sub}</span>
                        </div>
                      ))}
                    </div>

                    <Button variant="outline" onClick={previewICA} className="mb-6 bg-[#002FA7] text-white hover:bg-[#002075] border-[#002FA7] rounded-lg text-sm w-full justify-center" data-testid="preview-ica-btn">
                      <Download size={14} className="mr-2" /> Preview {agreementState?.state_name} Agreement (PDF)
                    </Button>

                    {/* Signature area */}
                    <div className="border-t-2 border-dashed border-slate-200 pt-6">
                      <div className="flex items-center gap-2 mb-4">
                        <AlertCircle size={16} className="text-amber-500" />
                        <span className="text-sm font-semibold text-slate-800">Electronic Signature</span>
                      </div>
                      <p className="text-xs text-slate-400 mb-4 leading-relaxed">By typing your full legal name below, you acknowledge that you have read and agree to all terms of the Independent Contractor Agreement.</p>
                      <Label className="text-xs font-medium text-slate-500 mb-1.5 block uppercase tracking-wider">Full Legal Name</Label>
                      <Input
                        value={sigName}
                        onChange={(e) => setSigName(e.target.value)}
                        placeholder={user?.name || "Type your full legal name"}
                        className="bg-white border-slate-200 text-slate-900 rounded-lg mb-4 font-serif italic text-xl h-12"
                        data-testid="ica-signature-input"
                      />
                      <Button onClick={handleSignICA} disabled={signing || !sigName.trim()} className="bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg px-6 py-2.5 w-full sm:w-auto" data-testid="sign-ica-btn">
                        {signing ? <><Loader2 size={14} className="animate-spin mr-2" /> Signing...</> : <>Sign Agreement <FileSignature size={14} className="ml-2" /></>}
                      </Button>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 3: Documents */}
            {activeStep === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}
                className="bg-white border border-slate-200 rounded-xl p-6 md:p-8" data-testid="onboarding-step-documents">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#EFF3FF] flex items-center justify-center">
                    <Upload size={22} className="text-[#002FA7]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-['Cabinet_Grotesk'] font-bold text-slate-900">Upload Documents</h3>
                    <p className="text-sm text-slate-400">Step 3 of 4</p>
                  </div>
                </div>
                {status?.documents_uploaded ? (
                  <div className="flex items-center gap-3 text-emerald-600 bg-emerald-50 border border-emerald-200 p-4 rounded-lg"><CheckCircle2 size={20} /><span className="text-sm font-medium">All documents uploaded</span></div>
                ) : (
                  <div>
                    <div className="space-y-3 mb-6">
                      {[
                        { name: 'Real Estate License', desc: 'Copy of your active license', required: true },
                        { name: 'Government Photo ID', desc: 'Driver\'s license or passport', required: true },
                        { name: 'W-9 Tax Form', desc: 'For tax reporting purposes', required: true },
                        { name: 'E&O Insurance', desc: 'Certificate of coverage (if applicable)', required: false },
                      ].map((doc) => (
                        <div key={doc.name} className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                          <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                            <Upload size={14} className="text-slate-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-slate-700">{doc.name}</span>
                              {doc.required && <span className="text-[8px] bg-red-50 text-red-400 px-1.5 py-0.5 rounded font-semibold uppercase">Required</span>}
                            </div>
                            <span className="text-[11px] text-slate-400">{doc.desc}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mb-4">Go to the <strong className="text-slate-600">Documents</strong> tab in the sidebar to upload each file, then return here.</p>
                    <Button onClick={() => completeStep('documents_uploaded')} className="bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg px-6 py-2.5" data-testid="complete-docs-btn">
                      I've Uploaded My Documents <ArrowRight size={14} className="ml-2" />
                    </Button>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 4: Orientation */}
            {activeStep === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}
                className="bg-white border border-slate-200 rounded-xl p-6 md:p-8" data-testid="onboarding-step-orientation">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#EFF3FF] flex items-center justify-center">
                    <GraduationCap size={22} className="text-[#002FA7]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-['Cabinet_Grotesk'] font-bold text-slate-900">Agent Orientation</h3>
                    <p className="text-sm text-slate-400">Step 4 of 4 — Final step!</p>
                  </div>
                </div>
                {status?.orientation_complete ? (
                  <div className="flex items-center gap-3 text-emerald-600 bg-emerald-50 border border-emerald-200 p-4 rounded-lg"><CheckCircle2 size={20} /><span className="text-sm font-medium">Orientation complete</span></div>
                ) : (
                  <div>
                    <p className="text-sm text-slate-500 mb-5">Review the checklist below to make sure you're set up for success:</p>
                    <div className="space-y-2.5 mb-6">
                      {[
                        'Understand the 90/10 commission structure and how payouts work',
                        'Review the available marketing campaigns in the Marketing tab',
                        'Explore Tools & Software links (MLS, DocuSign, Dotloop, etc.)',
                        'Make sure your agent profile is complete with license info',
                        'Know how to use the AI assistant for brokerage questions',
                      ].map((item, i) => (
                        <label key={`orient-${i}`} className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:border-[#002FA7]/20 transition-colors group">
                          <div className="w-5 h-5 rounded border-2 border-slate-300 flex items-center justify-center shrink-0 mt-0.5 group-hover:border-[#002FA7]/40 transition-colors">
                            <CheckCircle2 size={12} className="text-[#002FA7] opacity-0 group-hover:opacity-30 transition-opacity" />
                          </div>
                          <span className="text-sm text-slate-600 leading-snug">{item}</span>
                        </label>
                      ))}
                    </div>
                    <Button onClick={() => completeStep('orientation_complete')} className="bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg px-6 py-2.5" data-testid="complete-orientation-btn">
                      Complete Orientation <ArrowRight size={14} className="ml-2" />
                    </Button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
